import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getCategoryBySlug } from "@/lib/catalog";
import type { CategoryProduct, SortKey } from "@/lib/catalog";
import ProductItemCard from "@/components/feature/ProductItemCard";
import CategoryGridSkeleton from "@/pages/category/components/CategoryGridSkeleton";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "recent", label: "Más recientes" },
  { key: "price-asc", label: "Menor precio" },
  { key: "price-desc", label: "Mayor precio" },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug);

  const [products, setProducts] = useState<CategoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("recent");
  const [onlyNew, setOnlyNew] = useState(false);

  const fetchProducts = useCallback(async () => {
    if (!category) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("products")
        .select("*, profiles(full_name)")
        .eq("category", category.name)
        .eq("status", "active")
        .order("created_at", { ascending: false });
      if (err) throw err;
      setProducts((data ?? []) as unknown as CategoryProduct[]);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "No se pudieron cargar los anuncios de esta categoría.",
      );
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const visible = useMemo(() => {
    let list = [...products];
    if (onlyNew) {
      list = list.filter((p) => (p.condition ?? "").toLowerCase() === "nuevo");
    }
    if (sort === "price-asc") {
      list.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sort === "price-desc") {
      list.sort((a, b) => Number(b.price) - Number(a.price));
    }
    return list;
  }, [products, sort, onlyNew]);

  if (!category) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-background-50 px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100">
          <i className="ri-question-line text-3xl text-accent-700"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          Categoría no encontrada
        </h1>
        <p className="mt-1 max-w-sm text-sm text-foreground-500">
          La categoría que buscas no existe o el enlace cambió.
        </p>
        <Link
          to="/"
          className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Cabecera de la categoría */}
      <div className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-600">
              Inicio
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{category.name}</span>
          </nav>

          <div className="mt-4 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">
                {loading
                  ? "Cargando anuncios..."
                  : `${visible.length} ${
                      visible.length === 1 ? "anuncio disponible" : "anuncios disponibles"
                    }`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        {/* Filtros */}
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setOnlyNew((value) => !value)}
            className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              onlyNew
                ? "border-primary-300 bg-primary-50 text-primary-700"
                : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
            }`}
          >
            <i
              className={
                onlyNew
                  ? "ri-checkbox-circle-fill"
                  : "ri-checkbox-blank-circle-line"
              }
            ></i>
            Solo nuevos
          </button>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden whitespace-nowrap text-sm text-foreground-500 sm:inline">
              Ordenar por
            </span>
            <div className="relative">
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                className="cursor-pointer appearance-none rounded-md border border-background-200 bg-background-50 py-2 pl-3 pr-9 text-sm text-foreground-800 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              >
                {SORTS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500"></i>
            </div>
          </div>
        </div>

        {/* Contenido */}
        {loading ? (
          <CategoryGridSkeleton count={8} />
        ) : error ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-error-warning-line text-2xl text-accent-700"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-900">
              Ocurrió un error
            </p>
            <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={fetchProducts}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Reintentar
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-search-line text-2xl text-secondary-700"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-900">
              Aún no hay anuncios aquí
            </p>
            <p className="mt-1 max-w-sm text-sm text-foreground-500">
              No encontramos publicaciones activas en {category.name}
              {onlyNew ? " con el filtro aplicado" : ""}. Sé el primero en publicar.
            </p>
            <Link
              to="/registro"
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Publicar un anuncio
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map((product) => (
              <ProductItemCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}